import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Send, MessageCircle, Loader2 } from "lucide-react";

const categories = ["Account concern", "Payment concern", "Course access concern", "Technical concern", "Other"];
const statusColors = {
  open: "bg-blue-100 text-blue-800",
  pending: "bg-amber-100 text-amber-800",
  resolved: "bg-green-100 text-green-800",
};

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(iso).slice(0, 16).replace("T", " ");
  }
}

function InitialAvatar({ name, tone = "slate" }) {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  const bg = tone === "indigo"
    ? "bg-gradient-to-br from-indigo-500 to-violet-600 text-white"
    : "bg-gradient-to-br from-slate-500 to-slate-700 text-white";
  return (
    <div className={`h-8 w-8 rounded-full grid place-items-center text-xs font-bold shrink-0 ${bg}`}>
      {initial}
    </div>
  );
}

export default function StudentSupport() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", category: "Other" });
  const scrollRef = useRef(null);

  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data));
  useEffect(() => {
    load();
    // Mark support reply notifications as seen for the mobile More tab red dot.
    api.post("/student/notifications/mark-seen", { source: "support" }).catch(() => {});
  }, []);

  // Auto-scroll to newest message whenever detail loads or a new reply lands.
  useEffect(() => {
    if (!detail) return;
    const el = scrollRef.current;
    if (!el) return;
    // Defer to next tick so DOM has flushed the new bubbles first.
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [detail]);

  const submit = async () => {
    try {
      await api.post("/support/tickets", form);
      toast.success("Ticket submitted");
      setOpen(false); setForm({ subject: "", message: "", category: "Other" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openTicket = async (t) => {
    setView(t);
    setDetail(null);
    const r = await api.get(`/support/tickets/${t.id}`);
    setDetail(r.data);
  };

  const send = async () => {
    const body = reply.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${view.id}/reply`, { message: body });
      setReply("");
      const r = await api.get(`/support/tickets/${view.id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reply");
    } finally {
      setSending(false);
    }
  };

  const onReplyKeyDown = (e) => {
    // Enter sends, Shift+Enter inserts a newline (chat convention).
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="space-y-6" data-testid="student-support-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600">Help Center</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Support</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="new-ticket-btn"><Plus className="w-4 h-4 mr-2" />New Ticket</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Submit Support Ticket</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Message</Label><Textarea rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ticket-message" /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={submit} data-testid="submit-ticket-btn">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3">
        {tickets.length === 0 ? <Card className="p-8 text-center text-slate-500" data-testid="tickets-empty">No tickets yet</Card> :
          tickets.map((t) => (
            <Card key={t.id} className="p-5 border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => openTicket(t)} data-testid={`ticket-card-${t.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className={`${statusColors[t.status]} hover:opacity-90 capitalize`}>{t.status}</Badge>
                <Badge variant="outline">{t.category}</Badge>
                <span className="text-xs text-slate-500">{formatWhen(t.created_at)}</span>
              </div>
              <div className="font-semibold">{t.subject}</div>
              <div className="text-sm text-slate-600 line-clamp-1 mt-1">{t.message}</div>
            </Card>
          ))}
      </div>

      {/* Ticket detail — chat-style thread */}
      <Dialog open={!!view} onOpenChange={(v) => { if (!v) { setView(null); setDetail(null); setReply(""); } }}>
        <DialogContent
          className="max-w-2xl p-0 gap-0 flex flex-col h-[80vh] max-h-[720px]"
          data-testid="ticket-detail-dialog"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="pr-8 leading-tight">{view?.subject}</DialogTitle>
            {view && (
              <div className="flex items-center gap-2 flex-wrap pt-1">
                <Badge className={`${statusColors[view.status]} capitalize`}>{view.status}</Badge>
                <Badge variant="outline">{view.category}</Badge>
                <span className="text-[11px] text-slate-500">Opened {formatWhen(view.created_at)}</span>
              </div>
            )}
          </DialogHeader>

          {/* Scrollable chat area */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50 dark:bg-slate-950/40"
            data-testid="ticket-chat-scroll"
          >
            {!detail ? (
              <div className="h-full grid place-items-center text-slate-400 text-sm">
                <div className="flex flex-col items-center gap-2">
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Loading conversation…
                </div>
              </div>
            ) : (
              <>
                {/* Original message = student, aligned right */}
                <ChatBubble
                  side="right"
                  name={user?.full_name || "You"}
                  when={detail.ticket.created_at}
                  message={detail.ticket.message}
                  testId="ticket-msg-original"
                />
                {(detail.replies || []).map((r) => (
                  <ChatBubble
                    key={r.id}
                    side={r.sender_role === "student" ? "right" : "left"}
                    name={r.sender_name || (r.sender_role === "student" ? "You" : "Support")}
                    when={r.created_at}
                    message={r.message}
                    testId={`ticket-reply-${r.id}`}
                  />
                ))}
                {(!detail.replies || detail.replies.length === 0) && (
                  <div className="text-center text-xs text-slate-400 pt-4 flex flex-col items-center gap-2" data-testid="ticket-thread-empty">
                    <MessageCircle className="w-6 h-6 text-slate-300" />
                    Waiting for a support reply. We usually respond within a day.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sticky reply composer */}
          {view?.status !== "resolved" && (
            <div className="border-t border-slate-100 dark:border-slate-800 p-3 bg-white dark:bg-slate-900 shrink-0">
              <div className="flex items-end gap-2">
                <Textarea
                  rows={2}
                  placeholder="Type your reply…  (Enter = send, Shift+Enter = new line)"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={onReplyKeyDown}
                  className="resize-none flex-1"
                  data-testid="ticket-reply-input"
                />
                <Button
                  className="h-11 bg-[#1E3A8A] hover:bg-[#1E40AF] px-4"
                  onClick={send}
                  disabled={!reply.trim() || sending}
                  data-testid="ticket-reply-send-btn"
                  aria-label="Send reply"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}
          {view?.status === "resolved" && (
            <div className="border-t border-slate-100 p-3 bg-emerald-50 text-center text-xs text-emerald-800 font-semibold shrink-0" data-testid="ticket-resolved-notice">
              This ticket is resolved. Open a new ticket if you need more help.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Single chat bubble — indigo/right for student, slate/left for admin/support. */
function ChatBubble({ side, name, when, message, testId }) {
  const isRight = side === "right";
  return (
    <div className={`flex items-end gap-2 ${isRight ? "flex-row-reverse" : "flex-row"}`} data-testid={testId}>
      <InitialAvatar name={name} tone={isRight ? "indigo" : "slate"} />
      <div className={`max-w-[78%] ${isRight ? "items-end" : "items-start"} flex flex-col`}>
        <div
          className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap leading-snug shadow-sm ${
            isRight
              ? "bg-gradient-to-br from-indigo-600 to-violet-600 text-white rounded-br-sm"
              : "bg-white dark:bg-slate-800 dark:text-slate-100 text-slate-800 border border-slate-200 dark:border-slate-700 rounded-bl-sm"
          }`}
        >
          {message}
        </div>
        <div className={`text-[10px] text-slate-500 mt-1 px-1 ${isRight ? "text-right" : "text-left"}`}>
          {name} · {formatWhen(when)}
        </div>
      </div>
    </div>
  );
}
